import { i18nService } from '../../i18n/I18nService';
import { getHashParams } from '../../dsl/hashParams';
import { authService } from '../../auth/AuthService';
import { fetchPlaces } from '../../api/placesApi';
import { fetchAvailability, fetchSubscriptionBalance, createBooking, ApiError } from '../../api/bookingApi';
import { buildMonthDays } from '../calendarGrid';
import { createAdminContactsList } from '../adminContacts';
import { formatMinutesAsTime as formatTime, formatEuros, formatCentsAsEuros, dateKeyLocal as dateKey, isSameLocalDay as isSameDay } from '../../utils/format';
import { bookableWindows, createAvailabilityStatusRow, createSlotPicker, MAX_BOOKING_MINUTES, } from './slotPicker';
// Fallback used only before the first availability response arrives — once
// loaded, the server's own stepMinutes (server/availability.mjs) takes over,
// so a server-side change to the booking granularity doesn't silently
// desync from this hardcoded value.
const DEFAULT_STEP_MINUTES = 15;
// MAX_BOOKING_MINUTES (imported from slotPicker.ts, which mirrors the
// server's own constant — server/availability.mjs) is also this page's price
// break: at a full working day (8h) the flat day rate applies instead.
// Same 24h lead-time rule as cancellation (server/routes/booking.mjs's
// MIN_BOOKING_LEAD_SECONDS) — a slot can't be booked less than this far
// ahead of now.
const MIN_LEAD_SECONDS = 24 * 60 * 60;
function t(key, params) {
    return i18nService.t(key, params);
}
// Mirrors the server's parseDateParam: `dateStr` is a UTC calendar day, so a
// slot's instant is that UTC midnight plus its minutes-of-day offset.
function slotStartUnix(dateStr, startMinutes) {
    const [year, month, day] = dateStr.split('-').map(Number);
    return Date.UTC(year, month - 1, day) / 1000 + startMinutes * 60;
}
// Mirrors the server's price calculation (server/routes/booking.mjs) so the
// pre-submit estimate matches what will actually be charged. Up to 3 hours,
// billed proportionally at the hourly rate; beyond 3 hours, the first 3 hours
// are billed at the flat 3-hour rate and every hour past that is added at the
// hourly rate; at MAX_BOOKING_MINUTES (a full working day), the flat day rate
// applies instead. Falls back to the next tier down if a rate isn't set.
function computeHourlyPriceEuros(durationMinutes, pricePerHour, pricePer3Hours, pricePerDay) {
    if (pricePerDay != null && durationMinutes >= MAX_BOOKING_MINUTES) {
        return pricePerDay;
    }
    if (pricePer3Hours == null || durationMinutes < 180) {
        return (durationMinutes / 60) * pricePerHour;
    }
    return pricePer3Hours + ((durationMinutes - 180) / 60) * pricePerHour;
}
// crypto.randomUUID() is exposed only in a secure context, so it is missing on
// a plain-HTTP origin that isn't localhost (a LAN IP during phone testing, an
// HTTP deployment — see COOKIE_SECURE in .env.example). Letting it throw would
// leave the submit button stuck in its disabled "submitting" state, since the
// throw happens before the request whose .catch restores it.
// crypto.getRandomValues, unlike randomUUID, is available either way.
function createIdempotencyKey() {
    if (typeof crypto.randomUUID === 'function')
        return crypto.randomUUID();
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}
function buildErrorContent(message) {
    const wrapper = document.createElement('div');
    const status = document.createElement('p');
    status.className = 'booking__status';
    status.textContent = message;
    const link = document.createElement('a');
    link.href = '#page=home';
    link.className = 'booking__back-link';
    link.textContent = t('booking.backHomeLabel');
    wrapper.append(status, link);
    return wrapper;
}
export function renderBooking(_block) {
    const rawPlaceId = getHashParams().get('place');
    if (!authService.isAuthorized) {
        window.location.hash = rawPlaceId ? `#page=auth&place=${rawPlaceId}` : '#page=auth';
        return document.createElement('section');
    }
    const section = document.createElement('section');
    section.className = 'booking';
    const placeId = Number(rawPlaceId);
    if (rawPlaceId === null || !Number.isInteger(placeId)) {
        section.appendChild(buildErrorContent(t('booking.notFound')));
        return section;
    }
    const status = document.createElement('p');
    status.className = 'booking__status';
    status.textContent = t('booking.loading');
    section.appendChild(status);
    fetchPlaces('all')
        .then((places) => {
        const place = places.find((candidate) => candidate.id === placeId);
        section.innerHTML = '';
        if (!place) {
            section.appendChild(buildErrorContent(t('booking.notFound')));
            return;
        }
        section.appendChild(buildBookingUI(place));
    })
        .catch((error) => {
        console.error('Failed to load place:', error);
        section.innerHTML = '';
        section.appendChild(buildErrorContent(t('booking.error')));
    });
    return section;
}
function buildBookingUI(place) {
    const locale = i18nService.getLocale();
    const root = document.createElement('div');
    root.className = 'booking__content';
    // ---- Gallery ----
    const gallery = document.createElement('div');
    gallery.className = 'booking__gallery';
    const photo = document.createElement('img');
    photo.className = 'booking__gallery-photo';
    photo.src = place.photo ?? '';
    photo.alt = place.name[locale];
    gallery.appendChild(photo);
    root.appendChild(gallery);
    // ---- Title + meta ----
    const info = document.createElement('div');
    info.className = 'booking__info';
    const title = document.createElement('h1');
    title.className = 'section-title booking__title';
    title.textContent = place.name[locale];
    const meta = document.createElement('p');
    meta.className = 'booking__meta';
    meta.textContent = place.capacity === 1 ? t('booking.capacityOne') : t('booking.capacity', { count: place.capacity });
    info.append(title, meta);
    root.appendChild(info);
    root.appendChild(document.createElement('hr')).className = 'booking__divider';
    // ---- Calendar ----
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let viewYear = today.getFullYear();
    let viewMonth = today.getMonth();
    let selectedDate = new Date(today);
    const calendar = document.createElement('div');
    calendar.className = 'booking__cal';
    const calHead = document.createElement('div');
    calHead.className = 'booking__cal-head';
    const prevButton = document.createElement('button');
    prevButton.type = 'button';
    prevButton.className = 'booking__cal-nav';
    prevButton.setAttribute('aria-label', 'Previous month');
    prevButton.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 18l-6-6 6-6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    const nextButton = document.createElement('button');
    nextButton.type = 'button';
    nextButton.className = 'booking__cal-nav';
    nextButton.setAttribute('aria-label', 'Next month');
    nextButton.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    const monthLabel = document.createElement('span');
    monthLabel.className = 'booking__cal-month';
    calHead.append(prevButton, monthLabel, nextButton);
    const weekdaysRow = document.createElement('div');
    weekdaysRow.className = 'booking__cal-weekdays';
    for (let day = 0; day < 7; day += 1) {
        const cell = document.createElement('span');
        cell.className = 'booking__cal-weekday';
        cell.textContent = t(`booking.calendar.weekdays.${day}`);
        weekdaysRow.appendChild(cell);
    }
    const calGrid = document.createElement('div');
    calGrid.className = 'booking__cal-grid';
    calendar.append(calHead, weekdaysRow, calGrid);
    root.appendChild(calendar);
    root.appendChild(document.createElement('hr')).className = 'booking__divider';
    // ---- Availability + summary + sticky bar ----
    const availability = document.createElement('div');
    availability.className = 'booking__availability';
    root.appendChild(availability);
    const barSpace = document.createElement('div');
    barSpace.className = 'booking__bar-space';
    root.appendChild(barSpace);
    const bar = document.createElement('div');
    bar.className = 'booking__bar';
    const totalBlock = document.createElement('div');
    const totalLabel = document.createElement('p');
    totalLabel.className = 'booking__bar-label';
    totalLabel.textContent = t('booking.total');
    const totalPrice = document.createElement('p');
    totalPrice.className = 'booking__bar-price';
    totalPrice.textContent = '—';
    totalBlock.append(totalLabel, totalPrice);
    const submitButton = document.createElement('button');
    submitButton.type = 'button';
    submitButton.className = 'cta auth__submit booking__submit';
    submitButton.textContent = t('booking.submit');
    submitButton.disabled = true;
    bar.append(totalBlock, submitButton);
    root.appendChild(bar);
    const balanceLine = document.createElement('p');
    balanceLine.className = 'booking__balance-line';
    root.appendChild(balanceLine);
    const homeLink = document.createElement('a');
    homeLink.href = '#page=home';
    homeLink.className = 'booking__home-link';
    homeLink.textContent = t('booking.backHomeLabel');
    root.appendChild(homeLink);
    // The picker owns the active window / start / end state itself — read with
    // currentSelection() rather than keeping a second copy in sync here.
    let picker = null;
    let currentWindows = [];
    let currentStepMinutes = DEFAULT_STEP_MINUTES;
    let bookingConfirmed = false;
    let subscriptionLeftMinutes = undefined;
    let forceHourlyMode = false;
    // Reused across a retried submit (button re-enabled after a failed
    // request) for the same slot selection, so the server can dedupe a
    // double-submit instead of creating two bookings. Cleared whenever the
    // selected slot changes or a booking succeeds, since that's a new intent.
    let pendingIdempotencyKey = null;
    function currentSelection() {
        return picker?.getSelection() ?? { start: null, end: null };
    }
    function renderBalanceLine() {
        if (subscriptionLeftMinutes === undefined) {
            balanceLine.textContent = '';
            return;
        }
        if (subscriptionLeftMinutes !== null && subscriptionLeftMinutes > 0) {
            balanceLine.textContent = t('booking.subscriptionTimeLeft', { time: formatTime(subscriptionLeftMinutes) });
        }
        else if (place.pricePerHour != null) {
            balanceLine.textContent = t('booking.hourlyRateLabel', { price: formatEuros(place.pricePerHour) });
        }
        else {
            balanceLine.textContent = '';
        }
    }
    function hasMinLeadTime(startMinutes) {
        return slotStartUnix(dateKey(selectedDate), startMinutes) - Date.now() / 1000 >= MIN_LEAD_SECONDS;
    }
    // The picker renders the "Booking: HH:MM–HH:MM, N min" / placeholder
    // summary line itself (same booking.summary.* keys this used to duplicate)
    // — this only adds the price/subscription-warning parts around it.
    function renderSummaryAndBar() {
        const errorEl = availability.querySelector('.booking__error');
        if (errorEl)
            errorEl.remove();
        if (bookingConfirmed)
            return;
        const summary = availability.querySelector('.booking__summary');
        if (!summary)
            return;
        const warningEl = availability.querySelector('.booking__insufficient-warning');
        if (warningEl)
            warningEl.remove();
        const isUsingSubscription = subscriptionLeftMinutes !== undefined &&
            subscriptionLeftMinutes !== null &&
            subscriptionLeftMinutes > 0 &&
            !forceHourlyMode;
        totalBlock.style.display = isUsingSubscription ? 'none' : 'block';
        const { start, end } = currentSelection();
        if (start !== null && end !== null) {
            const duration = end - start;
            totalPrice.textContent =
                place.pricePerHour == null
                    ? '—'
                    : formatEuros(Math.round(computeHourlyPriceEuros(duration, place.pricePerHour, place.pricePer3Hours, place.pricePerDay) * 100) / 100);
            submitButton.disabled = false;
            if (subscriptionLeftMinutes !== undefined &&
                subscriptionLeftMinutes !== null &&
                subscriptionLeftMinutes > 0 &&
                subscriptionLeftMinutes < duration &&
                !forceHourlyMode) {
                const warning = document.createElement('p');
                warning.className = 'auth__error booking__insufficient-warning';
                warning.textContent = t('booking.insufficientWarning', {
                    available: formatTime(subscriptionLeftMinutes),
                    selected: formatTime(duration),
                });
                summary.insertAdjacentElement('afterend', warning);
            }
        }
        else {
            totalPrice.textContent = '—';
            submitButton.disabled = true;
        }
    }
    function renderAvailabilityBody() {
        availability.innerHTML = '';
        availability.appendChild(createAvailabilityStatusRow(dateKey(selectedDate), currentStepMinutes));
        if (currentWindows.length === 0) {
            picker = null;
            const emptyMessage = document.createElement('p');
            emptyMessage.className = 'booking__empty';
            emptyMessage.textContent = t('booking.availability.fullyBooked');
            availability.appendChild(emptyMessage);
            const summary = document.createElement('p');
            summary.className = 'booking__summary';
            availability.appendChild(summary);
            totalPrice.textContent = '—';
            submitButton.disabled = true;
            return;
        }
        picker = createSlotPicker({
            windows: currentWindows,
            stepMinutes: currentStepMinutes,
            onChange: () => {
                pendingIdempotencyKey = null;
                renderSummaryAndBar();
            },
        });
        availability.appendChild(picker.element);
        renderSummaryAndBar();
    }
    function showAvailabilityError(message) {
        picker = null;
        availability.innerHTML = '';
        const errorMessage = document.createElement('p');
        errorMessage.className = 'booking__empty';
        errorMessage.textContent = message;
        availability.appendChild(errorMessage);
        totalPrice.textContent = '—';
        submitButton.disabled = true;
    }
    function loadAvailability() {
        bookingConfirmed = false;
        // A different day is a new intent, exactly like switching window/start/end
        // below — reusing the key would make the server replay the booking made on
        // the previous day instead of booking this one.
        pendingIdempotencyKey = null;
        picker = null;
        submitButton.textContent = t('booking.submit');
        availability.innerHTML = `<p class="booking__status">${t('booking.loading')}</p>`;
        fetchAvailability(place.id, dateKey(selectedDate))
            .then((response) => {
            currentStepMinutes = response.stepMinutes;
            if (response.closed) {
                showAvailabilityError(t('booking.availability.closed'));
                return;
            }
            currentWindows = bookableWindows(response.windows);
            renderAvailabilityBody();
        })
            .catch((error) => {
            console.error('Failed to load availability:', error);
            showAvailabilityError(t('booking.error'));
        });
    }
    function renderCalendarGrid() {
        monthLabel.textContent = `${t(`booking.calendar.months.${viewMonth}`)} ${viewYear}`;
        calGrid.innerHTML = '';
        for (const date of buildMonthDays(viewYear, viewMonth)) {
            if (!date) {
                const empty = document.createElement('span');
                empty.className = 'booking__cal-day booking__cal-day--empty';
                calGrid.appendChild(empty);
                continue;
            }
            const isPast = date < today;
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'booking__cal-day';
            button.textContent = String(date.getDate());
            if (date.getDay() === 0 || date.getDay() === 6)
                button.classList.add('booking__cal-day--muted');
            if (isSameDay(date, today))
                button.classList.add('booking__cal-day--today');
            if (isSameDay(date, selectedDate))
                button.classList.add('booking__cal-day--selected');
            if (isPast) {
                button.classList.add('booking__cal-day--disabled');
                button.disabled = true;
            }
            else {
                button.addEventListener('click', () => {
                    selectedDate = date;
                    renderCalendarGrid();
                    loadAvailability();
                });
            }
            calGrid.appendChild(button);
        }
    }
    function openInsufficientTimeModal() {
        const overlay = document.createElement('div');
        overlay.className = 'booking__modal-overlay';
        overlay.addEventListener('click', (event) => {
            if (event.target === overlay)
                overlay.remove();
        });
        const modal = document.createElement('div');
        modal.className = 'booking__modal';
        const body = document.createElement('p');
        body.className = 'booking__modal-text';
        body.textContent = t('booking.insufficientTimeModal.body');
        const contactsLabel = document.createElement('p');
        contactsLabel.className = 'booking__modal-contacts-label';
        contactsLabel.textContent = t('booking.insufficientTimeModal.contactsLabel');
        const contacts = createAdminContactsList('booking__modal-contacts', 'booking__modal-contact-line');
        const actions = document.createElement('div');
        actions.className = 'booking__modal-actions';
        const closeButton = document.createElement('button');
        closeButton.type = 'button';
        closeButton.className = 'cta';
        closeButton.textContent = t('booking.insufficientTimeModal.closeButton');
        closeButton.addEventListener('click', () => overlay.remove());
        const hourlyButton = document.createElement('button');
        hourlyButton.type = 'button';
        hourlyButton.className = 'cta';
        hourlyButton.textContent = t('booking.insufficientTimeModal.hourlyButton');
        hourlyButton.addEventListener('click', () => {
            forceHourlyMode = true;
            overlay.remove();
            renderSummaryAndBar();
        });
        actions.append(hourlyButton, closeButton);
        modal.append(body, contactsLabel, contacts, actions);
        overlay.appendChild(modal);
        document.body.appendChild(overlay);
    }
    function openLeadTimeBlockedModal() {
        const overlay = document.createElement('div');
        overlay.className = 'booking__modal-overlay';
        overlay.addEventListener('click', (event) => {
            if (event.target === overlay)
                overlay.remove();
        });
        const modal = document.createElement('div');
        modal.className = 'booking__modal';
        const body = document.createElement('p');
        body.className = 'booking__modal-text';
        body.textContent = t('booking.leadTimeModal.body');
        const contactsLabel = document.createElement('p');
        contactsLabel.className = 'booking__modal-contacts-label';
        contactsLabel.textContent = t('booking.insufficientTimeModal.contactsLabel');
        const contacts = createAdminContactsList('booking__modal-contacts', 'booking__modal-contact-line');
        const actions = document.createElement('div');
        actions.className = 'booking__modal-actions';
        const okButton = document.createElement('button');
        okButton.type = 'button';
        okButton.className = 'cta';
        okButton.textContent = t('booking.leadTimeModal.okButton');
        okButton.addEventListener('click', () => overlay.remove());
        actions.appendChild(okButton);
        modal.append(body, contactsLabel, contacts, actions);
        overlay.appendChild(modal);
        document.body.appendChild(overlay);
    }
    prevButton.addEventListener('click', () => {
        viewMonth -= 1;
        if (viewMonth < 0) {
            viewMonth = 11;
            viewYear -= 1;
        }
        renderCalendarGrid();
    });
    nextButton.addEventListener('click', () => {
        viewMonth += 1;
        if (viewMonth > 11) {
            viewMonth = 0;
            viewYear += 1;
        }
        renderCalendarGrid();
    });
    submitButton.addEventListener('click', () => {
        const { start, end } = currentSelection();
        if (submitButton.disabled || start === null || end === null)
            return;
        if (!hasMinLeadTime(start)) {
            openLeadTimeBlockedModal();
            renderSummaryAndBar();
            return;
        }
        const duration = end - start;
        const insufficientSubscription = !forceHourlyMode &&
            subscriptionLeftMinutes !== undefined &&
            subscriptionLeftMinutes !== null &&
            subscriptionLeftMinutes > 0 &&
            subscriptionLeftMinutes < duration;
        if (insufficientSubscription && place.pricePerHour != null) {
            openInsufficientTimeModal();
            return;
        }
        submitButton.disabled = true;
        submitButton.textContent = t('booking.submitting');
        if (!pendingIdempotencyKey)
            pendingIdempotencyKey = createIdempotencyKey();
        createBooking({
            placeId: place.id,
            date: dateKey(selectedDate),
            startMinutes: start,
            endMinutes: end,
            locale: i18nService.getLocale(),
            forceHourly: forceHourlyMode,
            idempotencyKey: pendingIdempotencyKey,
        })
            .then((result) => {
            pendingIdempotencyKey = null;
            bookingConfirmed = true;
            submitButton.textContent = t('booking.submitted');
            if (result.booking.subscriptionLeftMinutes !== null) {
                subscriptionLeftMinutes = result.booking.subscriptionLeftMinutes;
                renderBalanceLine();
            }
            if (result.booking.paymentType === 'hourly' && result.booking.priceCents != null) {
                const note = document.createElement('p');
                note.className = 'booking__summary booking__pay-note';
                note.textContent = t('booking.payOnSiteNotice', { price: formatCentsAsEuros(result.booking.priceCents) });
                availability.appendChild(note);
            }
        })
            .catch((error) => {
            const code = error instanceof ApiError ? error.code : undefined;
            if (code === 'too_early') {
                openLeadTimeBlockedModal();
                submitButton.disabled = true;
                submitButton.textContent = t('booking.submit');
                return;
            }
            const messageKey = code === 'no_active_subscription'
                ? 'booking.errors.noSubscription'
                : code === 'slot_unavailable'
                    ? 'booking.errors.slotUnavailable'
                    : code === 'day_closed'
                        ? 'booking.errors.dayClosed'
                        : 'booking.errors.generic';
            const existingError = availability.querySelector('.booking__error');
            if (existingError)
                existingError.remove();
            const errorMessage = document.createElement('p');
            errorMessage.className = 'auth__error booking__error';
            errorMessage.textContent = t(messageKey);
            availability.appendChild(errorMessage);
            submitButton.disabled = false;
            submitButton.textContent = t('booking.submit');
        });
    });
    renderCalendarGrid();
    renderBalanceLine();
    loadAvailability();
    fetchSubscriptionBalance(place.id)
        .then((res) => {
        subscriptionLeftMinutes = res.leftMinutes;
        renderBalanceLine();
        renderSummaryAndBar();
    })
        .catch((error) => {
        console.error('Failed to load subscription balance:', error);
        subscriptionLeftMinutes = null;
        renderBalanceLine();
    });
    return root;
}
